import { keepPreviousData } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import type { ReactNode } from "react";
import {
  Button,
  Card,
  CardContent,
  Pagination,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@rumbledirect/ui-v2";
import type { SelectorSelectorQueryResourceQueryBriefOutputJsonld as SelectorQuery } from "@/lib/api/generated/model";
import { useGetAdminSelectorQueries } from "@/lib/api/generated/selector/selector";

const ITEMS_PER_PAGE = 30;
const SKELETON_KEYS = ["a", "b", "c"] as const;

export const Route = createFileRoute("/_authenticated/steering")({
  validateSearch: (search: Record<string, unknown>): { page: number } => {
    const page = Number(search.page);
    return { page: Number.isInteger(page) && page >= 1 ? page : 1 };
  },
  component: SteeringPage,
});

function SteeringPage(): ReactNode {
  const { page } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data, isPending, isError, refetch } = useGetAdminSelectorQueries(
    { page },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );
  const rows = data?.member ?? [];
  const status = isPending ? "pending" : isError ? "error" : "ready";
  const totalItems = data?.totalItems ?? rows.length;

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">
          Sturing
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Beheer selectieregels, klantafspraken en de inrichting van het
          sturingsproces.
        </p>
      </div>
      <Tabs value="selectors">
        <TabsList aria-label="Onderdelen van Sturing">
          <TabsTrigger value="selectors">Selectieregels</TabsTrigger>
          <TabsTrigger value="agreements" disabled>
            Klantafspraken
          </TabsTrigger>
          <TabsTrigger value="forms" disabled>
            Formulierconfiguratie
          </TabsTrigger>
          <TabsTrigger value="chain-test" disabled>
            Ketentest
          </TabsTrigger>
        </TabsList>
        <TabsContent value="selectors" className="mt-5">
          <SelectorList
            isPending={isPending}
            isError={isError}
            rows={rows}
            onRetry={() => void refetch()}
          />
          {totalItems > ITEMS_PER_PAGE && (
            <Pagination
              page={page}
              pageSize={ITEMS_PER_PAGE}
              totalItems={totalItems}
              status={status}
              onPageChange={(next) => void navigate({ search: { page: next } })}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

interface SelectorListProps {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly rows: readonly SelectorQuery[];
  readonly onRetry: () => void;
}

function SelectorList({
  isPending,
  isError,
  rows,
  onRetry,
}: SelectorListProps): ReactNode {
  if (isPending) {
    return (
      <div role="status" className="grid gap-3">
        <span className="sr-only">Selectieregels worden geladen…</span>
        {SKELETON_KEYS.map((key) => (
          <Skeleton key={key} className="h-16" />
        ))}
      </div>
    );
  }
  if (isError) {
    return (
      <div
        role="alert"
        className="rounded-2xl border border-destructive/30 bg-card p-8 text-center"
      >
        <p>De selectieregels konden niet worden geladen.</p>
        <Button className="mt-4" variant="outline" onClick={onRetry}>
          Opnieuw proberen
        </Button>
      </div>
    );
  }
  return (
    <ul className="grid gap-3">
      {rows.map((item) => (
        <li key={item.id}>
          <Link
            to="/steering/$queryId"
            params={{ queryId: item.id }}
            className="group block"
          >
            <Card className="transition-all group-hover:-translate-y-0.5 group-hover:border-primary/35 group-hover:shadow">
              <CardContent className="flex items-center gap-3 pt-5">
                <h2 className="min-w-0 flex-1 truncate font-display text-base font-semibold">
                  {item.name}
                </h2>
                <ArrowRight
                  className="size-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary"
                  aria-hidden="true"
                />
              </CardContent>
            </Card>
          </Link>
        </li>
      ))}
    </ul>
  );
}
